<template>
  <div class="browser-modal">
    <header class="browser-header">
      <Title size="5">
        Select a Document Type
      </Title>

      <IconButton
        class="browser-close"
        icon="fas fa-times-circle"
        @click="emit('close')"
      />
    </header>

    <div class="browser-scroller">
      <LoaderWithText v-if="isFetching" />

      <div
        v-else-if="groupedDocuments"
        class="browser-content"
      >
        <Menu>
          <MenuList>
            <MenuItem
              v-for="(category, key) in groupedDocuments"
              :key="key"
              :active="selectedCategoryId === key"
              @click="selectedCategoryId = key"
            >
              {{ category.category_display }}
            </MenuItem>
          </MenuList>
        </Menu>

        <div class="browser-scroller-body">
          <DocumentTypePicker
            :already-present="alreadyPresent"
            :document-types="selectedCategory.items"
            @addition="documentType => emit('addition', documentType)"
          />
        </div>
      </div>

      <div v-else>
        No document types found
      </div>
    </div>

    <div class="browser-footer">
      <Button
        variant="primary"
        spacing="loose"
        @click="emit('close')"
      >
        Done
      </Button>
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, onMounted, ref, watch } from 'vue';
import {
  Button,
  IconButton,
  LoaderWithText,
  Menu,
  MenuList,
  MenuItem,
  Title,
} from '@stakeui';
import DocumentTypePicker from './DocumentTypePicker.vue';

defineProps({
  alreadyPresent: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['addition', 'close', 'checklistLoaded']);

const isFetching = ref(true);
const documentTypes = ref([]);
const selectedCategoryId = ref(null);

const loadAvailableDocumentTypes = async () => {
  if (documentTypes.value.length > 0) return;

  isFetching.value = true;

  try {
    const params = new URLSearchParams({
      limit: 100,
    });
    const url = `/api/v1/client-portal/document-types/?${params.toString()}`;

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(
          `Failed to fetch available document types: ${response.status}`,
      );
    }

    const data = await response.json();
    documentTypes.value = data.document_types;
  } catch (err) {
    console.error('Error loading available document types:', err);
    documentTypes.value = [];
  } finally {
    isFetching.value = false;
  }
};

const groupedDocuments = computed(() => {
  return documentTypes.value.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = {
        category_display: item.category_display,
        items: [],
      };
    }
    acc[item.category].items.push(item);
    return acc;
  }, {});
});

const selectedCategory = computed(() => {
  if (!selectedCategoryId.value) return null;
  return groupedDocuments.value?.[selectedCategoryId.value] || null;
});

// Preselect first group when data is available
watch(groupedDocuments, newGroups => {
  if (!selectedCategoryId.value) {
    const keys = Object.keys(newGroups);

    if (keys.length > 0) {
      selectedCategoryId.value = keys[0];
    }
  }
}, { immediate: true });

onMounted(async () => {
  await loadAvailableDocumentTypes();
});
</script>

<style scoped>
.browser-modal {
  --spacing: 1.5em;
  max-width: 100%;
  width: 52em;
}

.browser-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing);
}

.browser-close {
  color: var(--stk-text-weak);
  font-size: 1.25em;
  transition-duration: var(--stk-transition-duration);
  transition-property: color;

  &:hover {
    color: var(--stk-text-normal);
  }
}

.browser-scroller {
  background-color: #fff;
  border-color: var(--stk-border);
  border-style: solid;
  border-width: 1px 0;
  box-shadow: inset 0 0.25em 0.5em rgb(0 0 0 / 5%);
  height: 30rem;
  overflow-y: scroll;
  overflow-x: hidden;
  padding: 1em;
}

.browser-content {
  display: grid;
  grid-template-columns: 16em 1fr;
  gap: 1em;
}

.browser-footer {
  display: flex;
  padding: var(--spacing);
  flex-direction: column;
}
</style>
